import qs from 'qs';
import client from './client';

// ============================================
// 제조관리 API
// ============================================

const BASE_URL = '/api/v1/manufacturing';

const normalizePageParam = (searchData = {}) => {
  if (searchData.page === 0) {
    return {
      ...searchData,
      page: 1,
    };
  }

  return searchData;
};

// ============================================
// 프로젝트 목록 (Projects)
// ============================================

/**
 * 수주 완료 프로젝트 목록 조회
 * @param {Object} searchData - { page, size, keyword, startDate, endDate }
 */
export const getCompletedProjects = (searchData) => {
  const queryString = qs.stringify(normalizePageParam(searchData));
  return client.get(`${BASE_URL}/projects?${queryString}`);
};

/**
 * 프로젝트 목록 + 제조 진행 요약 조회 (Plan A 방안)
 * @param {Object} searchData - { page, size, keyword, startDate, endDate }
 * @returns 프로젝트별 설계/자재/생산/QC 진행률 포함
 */
export const getProjectsWithSummary = (searchData) => {
  const queryString = qs.stringify(normalizePageParam(searchData));
  return client.get(`${BASE_URL}/projects/summary?${queryString}`);
};

/**
 * 프로젝트별 생산 계획 목록
 * @param {string} projectId - 프로젝트 UUID
 */
export const getProjectPlans = (projectId) => {
  return client.get(`${BASE_URL}/projects/${projectId}/plans`);
};

// ============================================
// 생산 계획 (Manufacturing Plans)
// ============================================

/**
 * 생산 계획 목록 조회
 * @param {Object} searchData - { page, size, status, keyword, projectId, startDate, endDate }
 */
export const getManufacturingPlans = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/plans?${queryString}`);
};

/**
 * 생산 계획 상세 조회
 * @param {string} planId - 생산계획 UUID
 */
export const getManufacturingPlan = (planId) => {
  return client.get(`${BASE_URL}/plans/${planId}`);
};

export const getManufacturingPlanById = getManufacturingPlan; // alias

/**
 * 생산 계획 번호로 조회
 * @param {string} planNumber - 생산계획 번호 (MANU-YYYYMMDD-XXXX)
 */
export const getManufacturingPlanByNumber = (planNumber) => {
  return client.get(`${BASE_URL}/plans/number/${planNumber}`);
};

/**
 * 프로젝트별 생산 계획 목록
 * @param {string} projectId - 프로젝트 UUID
 */
export const getManufacturingPlansByProject = (projectId) => {
  return client.get(`${BASE_URL}/projects/${projectId}/plans`);
};

/**
 * 생산 설정 조회 — 기본 담당자(이름 포함)와 생산 기능 사용 여부.
 *
 * 회사에 하나씩인 값이다. 메뉴 관리 화면의 "생산 설정" 탭에서 쓴다.
 */
export const getManufacturingSettings = () => {
  return client.get(`${BASE_URL}/settings`);
};

/**
 * 생산 설정 저장.
 * @param {Object} settings - { enabled, designUuid, productionUuid, qcUuid }
 */
export const saveManufacturingSettings = (settings) => {
  return client.put(`${BASE_URL}/settings`, settings);
};

/**
 * 이 프로젝트에 계획을 만들 때 추가 사유를 받아야 하는지.
 *
 * 생산이 끝난 프로젝트에 계획을 더 붙이는 경우에 참이 된다.
 * 사유 칸을 보여줄지 정하는 용도이고, 실제 검사는 저장할 때 서버가 다시 한다.
 *
 * @param {string} projectId - 프로젝트 UUID
 */
export const checkPlanAddReasonRequired = (projectId) => {
  return client.get(`${BASE_URL}/projects/${projectId}/plan-add-reason-required`);
};

/**
 * 생산 계획 생성
 * @param {Object} planData - { projectId, targetQuantity, planStartDate, planEndDate, remarks, addReason }
 */
export const createManufacturingPlan = (planData) => {
  return client.post(`${BASE_URL}/plans`, planData);
};

/**
 * 생산 계획 수정
 * @param {string} planId - 생산계획 UUID
 * @param {Object} planData - 수정할 데이터
 */
export const updateManufacturingPlan = (planId, planData) => {
  return client.put(`${BASE_URL}/plans/${planId}`, planData);
};

/**
 * 생산 계획 상태 변경
 * @param {string} planId - 생산계획 UUID
 * @param {Object} statusData - { status, remarks }
 */
export const updateManufacturingPlanStatus = (planId, statusData) => {
  return client.patch(`${BASE_URL}/plans/${planId}/status`, statusData);
};

/**
 * 생산 계획 삭제
 * @param {string} planId - 생산계획 UUID
 */
export const deleteManufacturingPlan = (planId) => {
  return client.delete(`${BASE_URL}/plans/${planId}`);
};

// ============================================
// 제조 공정 (Manufacturing Processes)
// ============================================

/**
 * 제조 공정 목록 조회
 * @param {Object} searchData - { page, size, planId, stage, status }
 */
export const getManufacturingProcesses = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/processes?${queryString}`);
};

/**
 * 제조 공정 상세 조회
 * @param {string} processId - 공정 UUID
 */
export const getManufacturingProcess = (processId) => {
  return client.get(`${BASE_URL}/processes/${processId}`);
};

/**
 * 생산 계획별 공정 목록
 * @param {string} planId - 생산계획 UUID
 */
export const getManufacturingProcessesByPlan = (planId) => {
  return client.get(`${BASE_URL}/processes/plan/${planId}`);
};

/**
 * 특정 단계의 공정 조회
 * @param {string} planId - 생산계획 UUID
 * @param {string} stage - 단계 (DESIGN/MATERIAL/PRODUCTION/QC/COMPLETE)
 */
export const getManufacturingProcessByStage = (planId, stage) => {
  return client.get(`${BASE_URL}/processes/plan/${planId}/stage/${stage}`);
};

/**
 * 제조 공정 생성
 * @param {Object} processData - { planId, stage, targetStartDate, targetEndDate, managerId, remarks }
 */
export const createManufacturingProcess = (processData) => {
  return client.post(`${BASE_URL}/processes`, processData);
};

/**
 * 제조 공정 수정
 * @param {string} processId - 공정 UUID
 * @param {Object} processData - 수정할 데이터
 */
export const updateManufacturingProcess = (processId, processData) => {
  return client.put(`${BASE_URL}/processes/${processId}`, processData);
};

/**
 * 제조 공정 상태 변경
 * @param {string} processId - 공정 UUID
 * @param {Object} statusData - { status, remarks }
 */
export const updateManufacturingProcessStatus = (processId, statusData) => {
  return client.patch(`${BASE_URL}/processes/${processId}/status`, statusData);
};

/**
 * 제조 공정 담당자 변경
 * @param {string} processId - 공정 UUID
 * @param {Object} managerData - { managerId }
 */
export const updateManufacturingProcessManager = (processId, managerData) => {
  return client.patch(`${BASE_URL}/processes/${processId}/manager`, managerData);
};

/**
 * 제조 공정 삭제
 * @param {string} processId - 공정 UUID
 */
export const deleteManufacturingProcess = (processId) => {
  return client.delete(`${BASE_URL}/processes/${processId}`);
};

// ============================================
// 설계 관리 (Designs)
// ============================================

/**
 * 설계 목록 조회
 * @param {Object} searchData - { page, size, processId, status, approvalStatus }
 */
export const getDesigns = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/designs?${queryString}`);
};

/**
 * 설계 상세 조회
 * @param {string} designId - 설계 UUID
 */
export const getDesign = (designId) => {
  return client.get(`${BASE_URL}/designs/${designId}`);
};

/**
 * 공정별 설계 조회
 * @param {string} processId - 공정 UUID
 */
export const getDesignsByProcess = (processId) => {
  return client.get(`${BASE_URL}/designs/process/${processId}`);
};

/**
 * 생산계획별 설계 목록 조회
 * @param {string} planId - 생산계획 UUID
 * @param {Object} searchData - { page, size, status }
 */
export const getDesignsByPlan = (planId, searchData = {}) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/designs/plan/${planId}?${queryString}`);
};

/**
 * 설계 생성
 * @param {Object} designData - { processId, productId, designVersion, quantity, unit, specifications, drawingFiles, designerId, remarks }
 */
export const createDesign = (designData) => {
  return client.post(`${BASE_URL}/designs`, designData);
};

/**
 * 설계 수정
 * @param {string} designId - 설계 UUID
 * @param {Object} designData - 수정할 데이터
 */
export const updateDesign = (designId, designData) => {
  return client.put(`${BASE_URL}/designs/${designId}`, designData);
};

/**
 * 설계 승인/반려
 * @param {string} designId - 설계 UUID
 * @param {Object} approvalData - { approvalStatus: 'APPROVED' | 'REJECTED', remarks }
 */
export const updateDesignApproval = (designId, approvalData) => {
  return client.patch(`${BASE_URL}/designs/${designId}/status`, {
    status:
      approvalData?.approvalStatus || approvalData?.status,
    reviewComment:
      approvalData?.remarks || approvalData?.reviewComment || '',
  });
};

/**
 * 설계 상태 변경
 * @param {string} designId - 설계 UUID
 * @param {Object} statusData - { status: 'PENDING' | 'IN_PROGRESS' | 'REVIEW' | 'APPROVED' | 'REJECTED', reviewComment }
 */
export const updateDesignStatus = (designId, statusData) => {
  return client.patch(`${BASE_URL}/designs/${designId}/status`, statusData);
};

/**
 * 설계 삭제
 * @param {string} designId - 설계 UUID
 */
export const deleteDesign = (designId) => {
  return client.delete(`${BASE_URL}/designs/${designId}`);
};

// ============================================
// 작업 지시 (Work Orders)
// ============================================

/**
 * 작업 지시 목록 조회
 * @param {Object} searchData - { page, size, processId, workType, status }
 */
export const getWorkOrders = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/work-orders?${queryString}`);
};

/**
 * 작업 지시 상세 조회
 * @param {string} workOrderId - 작업지시 UUID
 */
export const getWorkOrder = (workOrderId) => {
  return client.get(`${BASE_URL}/work-orders/${workOrderId}`);
};

/**
 * 작업 지시 생성
 * @param {Object} workOrderData - { processId, productId, targetQuantity, unit, workType, workStartDate, managerId, remarks }
 */
export const createWorkOrder = (workOrderData) => {
  return client.post(`${BASE_URL}/work-orders`, workOrderData);
};

/**
 * 작업 지시 상태 변경
 * @param {string} workOrderId - 작업지시 UUID
 * @param {Object} statusData - { status, endDate, remarks }
 */
export const updateWorkOrderStatus = (workOrderId, statusData) => {
  return client.patch(`${BASE_URL}/work-orders/${workOrderId}/status`, statusData);
};

/**
 * 작업 지시 삭제
 * @param {string} workOrderId - 작업지시 UUID
 */
export const deleteWorkOrder = (workOrderId) => {
  return client.delete(`${BASE_URL}/work-orders/${workOrderId}`);
};

// ============================================
// 생산 실적 (Production Results)
// ============================================

/**
 * 생산 실적 등록
 * @param {string} workOrderId - 작업지시 UUID
 * @param {Object} resultData - { producedQuantity, defectQuantity, workDate, workerId, remarks }
 */
export const createProductionResult = (workOrderId, resultData) => {
  return client.post(`${BASE_URL}/work-orders/${workOrderId}/results`, resultData);
};

/**
 * 작업 지시별 생산 실적 목록
 * @param {string} workOrderId - 작업지시 UUID
 */
export const getProductionResults = (workOrderId) => {
  return client.get(`${BASE_URL}/work-orders/${workOrderId}/results`);
};

// ============================================
// 자재 관리 (Materials) - 추후 구현
// ============================================

/**
 * 자재 소요 계획 목록 조회
 * @param {Object} searchData
 */
export const getMaterialRequirements = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/materials/requirements?${queryString}`);
};

/**
 * 자재 발주 목록 조회
 * @param {Object} searchData
 */
export const getMaterialOrders = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/materials/orders?${queryString}`);
};

/**
 * 자재 입고 목록 조회
 * @param {Object} searchData
 */
export const getMaterialReceipts = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/materials/receipts?${queryString}`);
};

// ============================================
// 외주 관리 (Outsourcing) - 추후 구현
// ============================================

/**
 * 외주 발주 목록 조회
 * @param {Object} searchData
 */
export const getOutsourcingOrders = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/outsourcing/orders?${queryString}`);
};

/**
 * 외주 입고 목록 조회
 * @param {Object} searchData
 */
export const getOutsourcingReceipts = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/outsourcing/receipts?${queryString}`);
};

// ============================================
// 생산 관리 (Productions)
// ============================================

/**
 * 생산계획별 생산 목록 조회
 * @param {string} planId - 생산계획 UUID
 * @param {Object} searchData - { page, size, status }
 */
export const getProductionsByPlan = (planId, searchData = {}) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/productions/plan/${planId}?${queryString}`);
};

/**
 * 생산 상세 조회
 * @param {string} productionId - 생산 UUID
 */
export const getProduction = (productionId) => {
  return client.get(`${BASE_URL}/productions/${productionId}`);
};

/**
 * 생산 생성
 * @param {Object} productionData
 */
export const createProduction = (productionData) => {
  return client.post(`${BASE_URL}/productions`, productionData);
};

/**
 * 생산 수정
 * @param {string} productionId
 * @param {Object} productionData
 */
export const updateProduction = (productionId, productionData) => {
  return client.put(`${BASE_URL}/productions/${productionId}`, productionData);
};

/**
 * 생산 상태 변경
 * @param {string} productionId
 * @param {Object} statusData - { status, remarks }
 */
export const updateProductionStatus = (productionId, statusData) => {
  return client.patch(`${BASE_URL}/productions/${productionId}/status`, statusData);
};

/**
 * 생산 삭제
 * @param {string} productionId
 */
export const deleteProduction = (productionId) => {
  return client.delete(`${BASE_URL}/productions/${productionId}`);
};

// ============================================
// 생산 완료 (Production Completion) - 추후 구현
// ============================================

/**
 * 생산 완료 목록 조회
 * @param {Object} searchData
 */
export const getProductionCompletions = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/completions?${queryString}`);
};

// ============================================
// 통계 (Statistics) - 추후 구현
// ============================================

/**
 * 생산 통계 조회
 * @param {Object} searchData - { startDate, endDate }
 */
export const getProductionStatistics = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/statistics/production?${queryString}`);
};

/**
 * 공정별 진행 현황
 * @param {Object} searchData
 */
export const getProcessStatusStatistics = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/statistics/process-status?${queryString}`);
};

// ============================================
// 출하 관리 (Shipments)
// ============================================

/**
 * 출하 목록 조회
 * @param {Object} searchData - { page, size, planId, status, keyword }
 */
export const getShipments = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/shipments?${queryString}`);
};

/**
 * 생산계획별 출하 목록 조회
 * @param {string} planId - 생산계획 UUID
 */
export const getShipmentsByPlan = (planId) => {
  return client.get(`${BASE_URL}/shipments/plan/${planId}`);
};

/**
 * 출하 상세 조회
 * @param {string} shipmentId - 출하 UUID
 */
export const getShipment = (shipmentId) => {
  return client.get(`${BASE_URL}/shipments/${shipmentId}`);
};

/**
 * 출하 등록
 * @param {Object} shipmentData - { planId, shipmentDate, quantity, remarks }
 */
export const createShipment = (shipmentData) => {
  return client.post(`${BASE_URL}/shipments`, shipmentData);
};

/**
 * 출하 수정
 * @param {string} shipmentId
 * @param {Object} shipmentData
 */
export const updateShipment = (shipmentId, shipmentData) => {
  return client.put(`${BASE_URL}/shipments/${shipmentId}`, shipmentData);
};

/**
 * 출하 상태 변경 (출하완료 처리)
 * @param {string} shipmentId
 * @param {Object} statusData - { status, remarks }
 */
export const updateShipmentStatus = (shipmentId, statusData) => {
  return client.patch(`${BASE_URL}/shipments/${shipmentId}/status`, statusData);
};

/**
 * 출하 삭제
 * @param {string} shipmentId
 */
export const deleteShipment = (shipmentId) => {
  return client.delete(`${BASE_URL}/shipments/${shipmentId}`);
};

// ============================================
// 완료 관리 (Completion)
// ============================================

/**
 * 완료 요약 조회
 * @param {string} planId - 생산계획 UUID
 */
export const getCompletionSummary = (planId) => {
  return client.get(`${BASE_URL}/completion/plan/${planId}/summary`);
};

/**
 * 생산 완료분을 창고로 넣는다.
 * 재고에 이 생산계획이 함께 적혀서, 나중에 출고할 때 주인을 따라갈 수 있다.
 * @param {string} planId
 * @param {{whUuid: string, quantity: number, inDate?: string, remarks?: string}} payload
 */
/**
 * 공정 완료 팝업에 미리 채울 작업 내용.
 * 빈 칸을 주면 대개 "작업완료" 만 남아서, 시스템이 아는 사실로 한 줄을 만들어 준다.
 * 실패해도 완료는 되어야 하므로 호출하는 쪽에서 막지 않는다.
 */
export const getProcessCompletionSummary = (routingDetUuid, planId) => {
  return client.get(`${BASE_URL}/routings/details/${routingDetUuid}/completion-summary`, {
    params: { planId },
  });
};

export const warehouseInFromProduction = (planId, payload) => {
  return client.post(`${BASE_URL}/completion/plan/${planId}/warehouse-in`, payload);
};

/**
 * 즉시 출고. 창고에 넣고 바로 내보내는 것을 서버가 한 번에 한다.
 * 이력에는 입고와 출고가 모두 남는다.
 */
export const shipNowFromProduction = (planId, payload) => {
  return client.post(`${BASE_URL}/completion/plan/${planId}/ship-now`, payload);
};

/**
 * 생산완료 처리
 * @param {string} planId
 */
export const completeProduction = (planId) => {
  return client.post(`${BASE_URL}/completion/plan/${planId}/complete-production`);
};

/**
 * 출하완료 처리
 * @param {string} planId
 */
export const completeShipment = (planId) => {
  return client.post(`${BASE_URL}/completion/plan/${planId}/complete-shipment`);
};

// ============================================
// 공정 마스터 (Process Master) - /api/v1/manufacturing/workflow
// ============================================

/**
 * 공정 마스터 목록 조회
 * @param {Object} searchData - { page, size, useYn, processTyp, keyword }
 */
export const getProcessMasters = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/workflow?${queryString}`);
};

/**
 * 사용 가능한 공정 목록 조회 (USE_YN = 'Y')
 */
export const getActiveProcesses = () => {
  return client.get(`${BASE_URL}/workflow/active`);
};

/**
 * 공정 상세 조회
 * @param {string} processUuid
 */
export const getProcessMaster = (processUuid) => {
  return client.get(`${BASE_URL}/workflow/${processUuid}`);
};

/**
 * 공정 코드로 조회
 * @param {string} processCd
 */
export const getProcessMasterByCode = (processCd) => {
  return client.get(`${BASE_URL}/workflow/code/${processCd}`);
};

/**
 * 공정 생성
 * @param {Object} processData
 */
export const createProcessMaster = (processData) => {
  return client.post(`${BASE_URL}/workflow`, processData);
};

/**
 * 공정 수정
 * @param {string} processUuid
 * @param {Object} processData
 */
export const updateProcessMaster = (processUuid, processData) => {
  return client.put(`${BASE_URL}/workflow/${processUuid}`, processData);
};

/**
 * 공정 삭제
 * @param {string} processUuid
 */
export const deleteProcessMaster = (processUuid) => {
  return client.delete(`${BASE_URL}/workflow/${processUuid}`);
};

// ============================================
// 워크플로우 (Workflows)
// ============================================

/**
 * 워크플로우 목록 조회
 * @param {Object} searchData - { page, size, useYn, keyword }
 */
export const getWorkflows = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/workflows?${queryString}`);
};

/**
 * 사용 가능한 워크플로우 목록 조회 (USE_YN = 'Y')
 */
export const getActiveWorkflows = () => {
  return client.get(`${BASE_URL}/workflows/active`);
};

/**
 * 기본 워크플로우 조회
 */
export const getDefaultWorkflow = () => {
  return client.get(`${BASE_URL}/workflows/default`);
};

/**
 * 기본 워크플로우 지정.
 * 지정한 것을 올리고 나머지를 내리는 것은 서버가 한 트랜잭션에서 한다.
 * @param {string} workflowUuid
 */
export const setDefaultWorkflow = (workflowUuid) => {
  return client.put(`${BASE_URL}/workflows/${workflowUuid}/default`);
};

/**
 * 이 워크플로우를 고르면 공정마다 누가 하게 되는지.
 *
 * 공정 마스터에 정해둔 팀·주/부 담당자와 그날 휴가를 반영한 <b>예정</b>이다.
 * 실제 배정은 공정이 만들어질 때 다시 계산하므로 그 사이 휴가가 생기면 달라질 수 있다.
 *
 * @param {string} workflowUuid
 * @param {string} [workDate] - 기준일(YYYY-MM-DD). 없으면 오늘
 */
export const getWorkflowAssignments = (workflowUuid, workDate) => {
  const qs = workDate ? `?workDate=${workDate}` : '';
  return client.get(`${BASE_URL}/workflows/${workflowUuid}/assignments${qs}`);
};

/**
 * 워크플로우 상세 조회 (공정 순서 포함)
 * @param {string} workflowUuid
 */
export const getWorkflow = (workflowUuid) => {
  return client.get(`${BASE_URL}/workflows/${workflowUuid}`);
};

/**
 * 워크플로우 코드로 조회
 * @param {string} workflowCd
 */
export const getWorkflowByCode = (workflowCd) => {
  return client.get(`${BASE_URL}/workflows/code/${workflowCd}`);
};

/**
 * 워크플로우 생성 (공정 순서 포함)
 * @param {Object} workflowData - { workflowNm, workflowDesc, details: [...] }
 */
export const createWorkflow = (workflowData) => {
  return client.post(`${BASE_URL}/workflows`, workflowData);
};

/**
 * 워크플로우 수정 (공정 순서 포함)
 * @param {string} workflowUuid
 * @param {Object} workflowData
 */
export const updateWorkflow = (workflowUuid, workflowData) => {
  return client.put(`${BASE_URL}/workflows/${workflowUuid}`, workflowData);
};

/**
 * 워크플로우 삭제
 * @param {string} workflowUuid
 */
export const deleteWorkflow = (workflowUuid) => {
  return client.delete(`${BASE_URL}/workflows/${workflowUuid}`);
};

/**
 * 워크플로우 공정 순서 조회
 * @param {string} workflowUuid
 */
export const getWorkflowDetails = (workflowUuid) => {
  return client.get(`${BASE_URL}/workflows/${workflowUuid}/details`);
};

// ============================================
// 제품 라우팅 (Product Routing)
// ============================================

/**
 * 라우팅 목록 조회
 * @param {Object} searchData - { page, size, planId, status, workflowUuid, keyword }
 */
export const getRoutings = (searchData) => {
  const queryString = qs.stringify(searchData);
  return client.get(`${BASE_URL}/routings?${queryString}`);
};

/**
 * 생산계획별 라우팅 목록 조회
 * @param {string} planId - 생산계획 UUID
 */
export const getRoutingsByPlan = (planId) => {
  return client.get(`${BASE_URL}/routings/plan/${planId}`);
};

/**
 * 라우팅 상세 조회 (공정 목록 포함)
 * @param {string} routingUuid
 */
export const getRouting = (routingUuid) => {
  return client.get(`${BASE_URL}/routings/${routingUuid}`);
};

/**
 * 워크플로우 기반 라우팅 생성
 * - 생산계획 생성 시 워크플로우 선택하면 라우팅 자동 생성
 * @param {Object} data - { planId, workflowUuid, productUuid }
 */
export const createRoutingFromWorkflow = (data) => {
  return client.post(`${BASE_URL}/routings/from-workflow`, data);
};

/**
 * 라우팅 생성
 * @param {Object} routingData
 */
export const createRouting = (routingData) => {
  return client.post(`${BASE_URL}/routings`, routingData);
};

/**
 * 라우팅 수정
 * @param {string} routingUuid
 * @param {Object} routingData
 */
export const updateRouting = (routingUuid, routingData) => {
  return client.put(`${BASE_URL}/routings/${routingUuid}`, routingData);
};

/**
 * 라우팅 삭제
 * @param {string} routingUuid
 */
export const deleteRouting = (routingUuid) => {
  return client.delete(`${BASE_URL}/routings/${routingUuid}`);
};

/**
 * 라우팅 공정 목록 조회
 * @param {string} routingUuid
 */
export const getRoutingDetails = (routingUuid) => {
  return client.get(`${BASE_URL}/routings/${routingUuid}/details`);
};

/**
 * 공정 상태 변경 (시작/완료)
 * - 모든 공정 완료 시 QC 검사 자동 생성
 * @param {string} routingDetUuid
 * @param {Object} statusData - { processStatus: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED' }
 */
export const updateRoutingProcessStatus = (routingDetUuid, statusData) => {
  return client.patch(`${BASE_URL}/routings/details/${routingDetUuid}/status`, statusData);
};

/**
 * 완품 QC 판정 (합격/불합격)
 * - 검사 공정(processMstTyp === 'INSPECTION')에서만 호출 가능
 * - 합격: 출하대상 자동 생성 / 불합격: 전량 폐기 대상(재작업 없음), 출하대상 생성 안 함
 * @param {string} routingDetUuid
 * @param {Object} judgeData - { result: 'PASS' | 'FAIL', remarks: string }
 */
export const judgeRoutingFinalQc = (routingDetUuid, judgeData) => {
  return client.put(`${BASE_URL}/routings/details/${routingDetUuid}/qc-judge`, judgeData);
};

/**
 * 라우팅 공정 수정
 * @param {string} routingDetUuid
 * @param {Object} detailData
 */
export const updateRoutingDetail = (routingDetUuid, detailData) => {
  return client.put(`${BASE_URL}/routings/details/${routingDetUuid}`, detailData);
};

/**
 * 라우팅 공정 삭제
 * @param {string} routingDetUuid
 */
export const deleteRoutingDetail = (routingDetUuid) => {
  return client.delete(`${BASE_URL}/routings/details/${routingDetUuid}`);
};

// ============================================
// 외주 관리 (Outsource)
// ============================================

/** 생산계획별 외주 목록 */
export const getOutsourcesByPlan = (planId) =>
  client.get(`${BASE_URL}/outsource/plan/${planId}`);

/** 공정별 외주 정보 */
export const getOutsourceByRouting = (routingDetUuid) =>
  client.get(`${BASE_URL}/outsource/routing/${routingDetUuid}`);

/** 외주 상세 */
export const getOutsource = (outsourceUuid) =>
  client.get(`${BASE_URL}/outsource/${outsourceUuid}`);

/** 외주 등록 */
export const createOutsource = (data) =>
  client.post(`${BASE_URL}/outsource`, data);

/** 외주 수정 */
export const updateOutsource = (outsourceUuid, data) =>
  client.put(`${BASE_URL}/outsource/${outsourceUuid}`, data);

/** 외주 상태 변경 */
export const updateOutsourceStatus = (outsourceUuid, status) =>
  client.patch(`${BASE_URL}/outsource/${outsourceUuid}/status`, { status });

/** 외주 취소 */
export const cancelOutsource = (outsourceUuid) =>
  client.patch(`${BASE_URL}/outsource/${outsourceUuid}/cancel`);

/** 외주 삭제 */
export const deleteOutsource = (outsourceUuid) =>
  client.delete(`${BASE_URL}/outsource/${outsourceUuid}`);
